// src/components/users/EquipmentItemCard.tsx

import React from 'react';
import { Assignment } from '../../types/assignment';

interface EquipmentItemCardProps {
  assignment: Assignment;
  onReturn: (assignment: Assignment) => void;
}

// Equipment category icons
const EQUIPMENT_ICONS: Record<string, string> = {
  'firearms': '🔫',
  'firearm': '🔫',
  'weapon': '🔫',
  'protective': '🛡️',
  'armor': '🛡️',
  'vest': '🛡️',
  'communication': '📻',
  'radio': '📻',
  'vehicle': '🚗',
  'uniform': '👕',
  'equipment': '📋',
  'tool': '🔧',
  'medical': '🏥',
  'default': '📦'
};

const getEquipmentIcon = (itemName: string, category?: string): string => {
  const searchText = `${itemName} ${category || ''}`.toLowerCase();
  
  for (const [key, icon] of Object.entries(EQUIPMENT_ICONS)) {
    if (searchText.includes(key)) {
      return icon;
    }
  }
  return EQUIPMENT_ICONS.default;
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

// Check if body armor expiration is approaching (within 6 months)
const getExpirationWarning = (expirationDate?: string): { isExpiring: boolean; isExpired: boolean; daysUntil: number } => {
  if (!expirationDate) return { isExpiring: false, isExpired: false, daysUntil: 0 };
  
  const expiry = new Date(expirationDate);
  const today = new Date();
  const daysUntil = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  
  return {
    isExpiring: daysUntil <= 180 && daysUntil > 0, // Within 6 months
    isExpired: daysUntil <= 0,
    daysUntil
  };
};

export const EquipmentItemCard: React.FC<EquipmentItemCardProps> = ({
  assignment,
  onReturn
}) => {
  const icon = getEquipmentIcon(assignment.itemName);
  const hasSerial = assignment.itemSerialNumber;
  
  // Mock body armor expiration - in real app, this would come from inventory item data
  const isBodyArmor = assignment.itemName.toLowerCase().includes('armor') || 
                     assignment.itemName.toLowerCase().includes('vest');
  const mockExpirationDate = isBodyArmor ? '2025-03-15' : undefined;
  const expiration = getExpirationWarning(mockExpirationDate);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {/* Item name with icon */}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">{icon}</span>
            <h3 className="font-medium text-gray-900">{assignment.itemName}</h3>
          </div>
          
          {/* Serial number */}
          {hasSerial && (
            <div className="text-sm text-gray-600 mb-1">
              <span className="font-medium">Serial:</span> {assignment.itemSerialNumber}
            </div>
          )}
          
          {/* Body armor expiration warning */}
          {isBodyArmor && mockExpirationDate && (
            <div className={`text-sm mb-1 flex items-center gap-1 ${
              expiration.isExpired 
                ? 'text-red-600 font-medium' 
                : expiration.isExpiring 
                  ? 'text-amber-600 font-medium' 
                  : 'text-gray-600'
            }`}>
              {expiration.isExpired && <span>🚨</span>}
              {expiration.isExpiring && !expiration.isExpired && <span>⚠️</span>}
              <span>
                {expiration.isExpired 
                  ? `Expired ${Math.abs(expiration.daysUntil)} days ago`
                  : expiration.isExpiring 
                    ? `Expires ${formatDate(mockExpirationDate)} (${expiration.daysUntil} days)`
                    : `Expires ${formatDate(mockExpirationDate)}`
                }
              </span>
            </div>
          )}
          
          {/* Assignment date */}
          <div className="text-sm text-gray-500">
            Assigned: {formatDate(assignment.assignmentDate)}
          </div>
        </div>
        
        {/* Return button */}
        <button
          onClick={() => onReturn(assignment)}
          className="ml-4 px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
        >
          Return
        </button>
      </div>
    </div>
  );
};